// ProfessionalsPage.jsx

// Dependencies
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getProfessionals, addProfessional } from '@/services/schedulingService';
import PageHeading from '@/components/common/PageHeading';
import Icon from '@/components/common/Icon';
import Professionals from '@/components/data/professionals/Professionals';
import LoadingOverlay from '@/components/common/LoadingOverlay';

// Styles
import { professionalspageCss } from '@digital_monorepo/styling';

const FUNCTION_LEVELS = [
  { value: '', label: '— select level —' },
  { value: '900701', label: 'Equity Partner' },
  { value: '900105', label: 'Salary Partner 4' },
  { value: '900104', label: 'Salary Partner 3' },
  { value: '900103', label: 'Salary Partner 2' },
  { value: '800105', label: 'Associate Partner 5' },
  { value: '800104', label: 'Associate Partner 4' },
  { value: '800103', label: 'Associate Partner 3' },
  { value: '800102', label: 'Associate Partner 2' },
  { value: '800101', label: 'Associate Partner 1' },
  { value: '800304', label: 'Tech Director 4' },
  { value: '800303', label: 'Tech Director 3' },
  { value: '700106', label: 'Senior Manager 6' },
  { value: '700104', label: 'Senior Manager 4' },
  { value: '700101', label: 'Senior Manager 1' },
  { value: '700403', label: 'Master Lead Specialist 3' },
  { value: '700301', label: 'Tech Senior Manager 1' },
  { value: '602204', label: 'Tech Manager 4' },
  { value: '602203', label: 'Tech Manager 3' },
  { value: '602202', label: 'Tech Manager 2' },
  { value: '602201', label: 'Tech Manager 1' },
  { value: '600205', label: 'Manager 5' },
  { value: '600204', label: 'Manager 4' },
  { value: '600203', label: 'Manager 3' },
  { value: '600202', label: 'Manager 2' },
  { value: '600201', label: 'Manager 1' },
  { value: '503602', label: 'Tech Experienced Senior 2' },
  { value: '503601', label: 'Tech Experienced Senior 1' },
  { value: '503503', label: 'Tech Senior 3' },
  { value: '503502', label: 'Tech Senior 2' },
  { value: '503501', label: 'Tech Senior 1' },
  { value: '500106', label: 'Senior Consultant 6' },
  { value: '500104', label: 'Senior Consultant 4' },
  { value: '500103', label: 'Senior Consultant 3' },
  { value: '500102', label: 'Senior Consultant 2' },
  { value: '500101', label: 'Senior Consultant 1' },
  { value: '403104', label: 'Tech Consultant 4' },
  { value: '403103', label: 'Tech Consultant 3' },
  { value: '403102', label: 'Tech Consultant 2' },
  { value: '403101', label: 'Tech Consultant 1' },
  { value: '401102', label: 'Consultant 2' },
  { value: '401101', label: 'Consultant 1' },
  { value: '303201', label: 'Technician' },
  { value: '302101', label: 'Tech Analyst' },
  { value: '300101', label: 'Analyst' }
];

const OFFICES = ['Lisbon Studio', 'Bom Sucesso', 'Braga', 'Talatona', 'Oporto Studio'];

const emptyForm = () => ({
  ProfessionalID: '',
  ProfessionalName: '',
  FunctionLevel: '',
  FunctionName: '',
  Office: '',
  ProfessionalRC: '',
  Skills: '',
  area: ''
});

const fieldRow = { marginBottom: 14 };
const fieldLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 };
const fieldInput = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  boxSizing: 'border-box'
};
const fieldSelect = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  boxSizing: 'border-box',
  background: '#fff'
};
const modalFooter = { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 };
const cancelBtn = {
  padding: '8px 18px',
  border: '1px solid #ccc',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600
};
const saveBtn = {
  padding: '8px 18px',
  border: 'none',
  borderRadius: 4,
  background: '#25634b',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700
};

const ProfessionalsPage = () => {
  const { t } = useTranslation();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOffice, setFilterOffice] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchProfessionals = async () => {
    try {
      const professionalsData = await getProfessionals();
      setProfessionals(professionalsData);
    } catch (error) {
      console.error('Error fetching professionals:', error);
      setError('Failed to load professionals data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const handleFormChange = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'FunctionLevel') {
        const match = FUNCTION_LEVELS.find(f => f.value === value);
        next.FunctionName = match ? match.label : '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.ProfessionalID.trim()) {
      setFormError('Professional ID is required.');
      return;
    }
    if (!form.ProfessionalName.trim()) {
      setFormError('Professional Name is required.');
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const body = { ...form };
      Object.keys(body).forEach(k => {
        if (body[k] === '') delete body[k];
      });
      await addProfessional(body);
      setShowModal(false);
      setForm(emptyForm());
      setLoading(true);
      await fetchProfessionals();
    } catch (e) {
      console.error('Error adding professional:', e);
      setFormError(e?.response?.data?.message || 'Failed to create professional. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get unique offices for filter
  const offices = [...new Set(professionals.map(p => p.Office))].filter(Boolean);

  // Filter professionals based on search and office
  const filteredProfessionals = professionals.filter(professional => {
    const matchesSearch =
      !searchTerm ||
      professional.ProfessionalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(professional.ProfessionalID || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      professional.FunctionName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesOffice = !filterOffice || professional.Office === filterOffice;

    return matchesSearch && matchesOffice;
  });

  if (error) {
    return (
      <div className={`${professionalspageCss.container} professional-directory`}>
        <div className={professionalspageCss.header}>
          <h1 className={professionalspageCss.title}>{t('PROFESSIONALS_TABLE.TITLE') || 'Team Members'}</h1>
          <p className={professionalspageCss.subtitle}>Error loading data</p>
        </div>
        <div className={professionalspageCss.errorContainer}>
          <h3 className={professionalspageCss.errorTitle}>⚠️ Unable to Load Data</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className={professionalspageCss.errorButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${professionalspageCss.container} professional-directory`}>
      {loading && <LoadingOverlay />}

      {/* Add Professional Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered scrollable className='workspace-dialog' aria-labelledby='add-professional-title'>
        <Modal.Header closeButton>
          <Modal.Title id='add-professional-title'>Add professional</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-ProfessionalID'>
              Professional ID *
            </label>
            <input id='new-professional-ProfessionalID' style={fieldInput} value={form.ProfessionalID} onChange={e => handleFormChange('ProfessionalID', e.target.value)} placeholder='e.g. T012345' />
          </div>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-ProfessionalName'>
              Full Name *
            </label>
            <input
              id='new-professional-ProfessionalName'
              style={fieldInput}
              value={form.ProfessionalName}
              onChange={e => handleFormChange('ProfessionalName', e.target.value)}
              placeholder='e.g. João Silva'
            />
          </div>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-FunctionLevel'>
              Function Level
            </label>
            <select id='new-professional-FunctionLevel' style={fieldSelect} value={form.FunctionLevel} onChange={e => handleFormChange('FunctionLevel', e.target.value)}>
              {FUNCTION_LEVELS.map(f => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-Office'>
              Office
            </label>
            <select id='new-professional-Office' style={fieldSelect} value={form.Office} onChange={e => handleFormChange('Office', e.target.value)}>
              <option value=''>— select office —</option>
              {OFFICES.map(o => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-ProfessionalRC'>
              RC
            </label>
            <input id='new-professional-ProfessionalRC' style={fieldInput} value={form.ProfessionalRC} onChange={e => handleFormChange('ProfessionalRC', e.target.value)} placeholder='e.g. T51NG' />
          </div>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-Skills'>
              Skills
            </label>
            <input id='new-professional-Skills' style={fieldInput} value={form.Skills} onChange={e => handleFormChange('Skills', e.target.value)} placeholder='e.g. Guidewire, Testing' />
          </div>
          <div style={fieldRow}>
            <label style={fieldLabel} htmlFor='new-professional-area'>
              Area
            </label>
            <select id='new-professional-area' style={fieldSelect} value={form.area} onChange={e => handleFormChange('area', e.target.value)}>
              <option value=''>— select area —</option>
              <option value='TC'>TC – Technology Consulting</option>
              <option value='BC'>BC – Business Consulting</option>
              <option value='TO'>TO – Technology Operate</option>
              <option value='Contractor'>Contractor</option>
            </select>
          </div>

          {formError && <div style={{ color: '#c00', fontSize: 12, marginBottom: 10 }}>{formError}</div>}

          <div style={modalFooter}>
            <button
              style={cancelBtn}
              onClick={() => {
                setShowModal(false);
                setForm(emptyForm());
                setFormError('');
              }}>
              Cancel
            </button>
            <button style={saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Add Professional'}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <PageHeading title='Professionals'>
        <span className='workspace-count'>{professionals.length} professionals</span>
        <button
          className='workspace-button'
          onClick={() => {
            setForm(emptyForm());
            setFormError('');
            setShowModal(true);
          }}>
          <Icon name='plus' size={16} />
          Add Professional
        </button>
      </PageHeading>
      <div className='workspace-toolbar directory-toolbar'>
        <label className='workspace-search'>
          <Icon name='search' size={17} />
          <input type='search' aria-label='Search professionals' placeholder='Search by name, ID or role…' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </label>
        <select aria-label='Filter by office' value={filterOffice} onChange={e => setFilterOffice(e.target.value)}>
          <option value=''>All offices</option>
          {offices.sort().map(o => (
            <option key={o}>{o}</option>
          ))}
        </select>
        {(searchTerm || filterOffice) && (
          <button
            className='workspace-button workspace-button-quiet'
            onClick={() => {
              setSearchTerm('');
              setFilterOffice('');
            }}>
            Clear filters
          </button>
        )}
        <span className='workspace-toolbar-hint'>{filteredProfessionals.length} results</span>
      </div>

      {/* Results Info */}
      {(searchTerm || filterOffice) && (
        <div className={professionalspageCss.resultsInfo}>
          Showing {filteredProfessionals.length} of {professionals.length} professionals
        </div>
      )}

      {/* Main Content */}
      <div className={professionalspageCss.contentCard}>
        <Professionals data={filteredProfessionals} onRefresh={fetchProfessionals} />
      </div>
    </div>
  );
};

export default ProfessionalsPage;
